import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardSubtitle,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonNote,
    IonPage,
    IonTextarea,
    IonTitle,
    IonToolbar
} from "@ionic/react";
import React, {useMemo, useState} from "react";
import createAxiosInstance from "../../services/AxiosInstance";
import {useToast} from "../../utils/useToasts";
import "./AdminPushNotifications.css";

const PUSH_BROADCAST_ENDPOINT = '/admin/push-notifications/test/all-devices';

const parseAdditionalPushData = (rawValue: string): Record<string, string> => {
    if (!rawValue.trim()) {
        return {};
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
        throw new Error('invalid-extra-data');
    }

    return Object.entries(parsed).reduce<Record<string, string>>((accumulator, [key, value]) => {
        accumulator[key] = String(value);
        return accumulator;
    }, {});
};

const AdminPushNotifications: React.FC = () => {
    const axios = useMemo(() => createAxiosInstance(), []);
    const showToasts = useToast();
    const [pushTitle, setPushTitle] = useState('Broadcast test');
    const [pushBody, setPushBody] = useState('Push notifications are working everywhere');
    const [pushUrl, setPushUrl] = useState('/dertigen');
    const [pushExtraData, setPushExtraData] = useState('');
    const [isSendingPush, setIsSendingPush] = useState(false);

    const handleBroadcastPush = async () => {
        if (!pushTitle.trim() || !pushBody.trim()) {
            showToasts('Vul minimaal een titel en bericht in', 'danger');
            return;
        }

        setIsSendingPush(true);

        try {
            const additionalData = parseAdditionalPushData(pushExtraData);
            const data = {
                ...additionalData,
                ...(pushUrl.trim() ? {url: pushUrl.trim()} : {}),
            };

            await axios.post(PUSH_BROADCAST_ENDPOINT, {
                title: pushTitle.trim(),
                body: pushBody.trim(),
                data,
            });

            showToasts('Push broadcast verstuurd', 'success');
        } catch (error) {
            if (error instanceof SyntaxError || (error instanceof Error && error.message === 'invalid-extra-data')) {
                showToasts('Extra data moet geldige JSON zijn, bijvoorbeeld {"type":"broadcast"}', 'danger');
            } else {
                showToasts('Push broadcast versturen mislukt', 'danger');
            }
        } finally {
            setIsSendingPush(false);
        }
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/admin"/>
                    </IonButtons>
                    <IonTitle>Push broadcast</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen className="ion-padding">
                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>Alle geregistreerde apparaten</IonCardTitle>
                        <IonCardSubtitle>Stel een payload op en verstuur die via het admin endpoint</IonCardSubtitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div className="push-broadcast-form">
                            <IonItem>
                                <IonLabel position="stacked">Titel</IonLabel>
                                <IonInput
                                    value={pushTitle}
                                    onIonInput={(event) => setPushTitle(event.detail.value ?? '')}
                                    placeholder="Broadcast test"
                                />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Bericht</IonLabel>
                                <IonTextarea
                                    value={pushBody}
                                    onIonInput={(event) => setPushBody(event.detail.value ?? '')}
                                    autoGrow
                                    placeholder="Push notifications are working everywhere"
                                />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Doel URL</IonLabel>
                                <IonInput
                                    value={pushUrl}
                                    onIonInput={(event) => setPushUrl(event.detail.value ?? '')}
                                    placeholder="/dertigen"
                                />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Extra data JSON</IonLabel>
                                <IonTextarea
                                    value={pushExtraData}
                                    onIonInput={(event) => setPushExtraData(event.detail.value ?? '')}
                                    autoGrow
                                    placeholder='{"type":"broadcast"}'
                                />
                            </IonItem>
                            <IonNote className="push-broadcast-endpoint">
                                POST {PUSH_BROADCAST_ENDPOINT}
                            </IonNote>
                            <IonButton expand="block" onClick={handleBroadcastPush} disabled={isSendingPush}>
                                {isSendingPush ? 'Versturen...' : 'Push broadcast versturen'}
                            </IonButton>
                        </div>
                    </IonCardContent>
                </IonCard>
            </IonContent>
        </IonPage>
    );
};

export default AdminPushNotifications;